import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Card, PageHeader } from '../components/ui';
import { extractWatermarkFromFile } from '../utils/steganography';
import './WatermarkPage.css';

type State =
  | { phase: 'idle' }
  | { phase: 'reading' }
  | { phase: 'found'; userId: string; decodedAt: Date; filename: string }
  | { phase: 'empty'; filename: string }
  | { phase: 'error'; message: string };

export function WatermarkPage() {
  const [state, setState] = useState<State>({ phase: 'idle' });
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleFile(file: File) {
    if (!file.type.startsWith('image/')) {
      setState({ phase: 'error', message: 'Please upload an image file (PNG recommended).' });
      return;
    }
    setState({ phase: 'reading' });
    try {
      const userId = await extractWatermarkFromFile(file);
      if (userId) {
        setState({ phase: 'found', userId, decodedAt: new Date(), filename: file.name });
      } else {
        setState({ phase: 'empty', filename: file.name });
      }
    } catch (err) {
      setState({ phase: 'error', message: (err as Error).message || 'Failed to read image.' });
    }
  }

  function onInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    e.target.value = '';
  }

  function onDrop(e: React.DragEvent) {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }

  function reset() {
    setState({ phase: 'idle' });
  }

  return (
    <div>
      <PageHeader
        eyebrow="12 — WATERMARK"
        title={<>Image <em>forensics</em>.</>}
        lede="Upload a shared image to extract the invisible user ID watermark embedded at share time."
      />

      <Card>
        {(state.phase === 'idle' || state.phase === 'error') && (
          <>
            <label
              className="wm-dropzone"
              onDragOver={e => e.preventDefault()}
              onDrop={onDrop}
            >
              <input
                ref={inputRef}
                type="file"
                accept="image/*"
                onChange={onInputChange}
                hidden
              />
              <div className="wm-dropzone__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                  <polyline points="17 8 12 3 7 8" />
                  <line x1="12" y1="3" x2="12" y2="15" />
                </svg>
              </div>
              <strong>Drop a shared image here</strong>
              <span>or click to browse · PNG files only retain watermarks</span>
            </label>
            {state.phase === 'error' && (
              <div className="wm-error">{state.message}</div>
            )}
          </>
        )}

        {state.phase === 'reading' && (
          <div className="wm-reading">
            <div className="wm-reading__spinner" />
            Scanning image for watermark…
          </div>
        )}

        {state.phase === 'found' && (
          <div className="wm-result wm-result--found">
            <div className="wm-result__badge">Watermark detected</div>
            <div className="wm-result__filename">{state.filename}</div>
            <div className="wm-result__row">
              <span className="wm-result__label">User ID</span>
              <code className="wm-result__value">{state.userId}</code>
            </div>
            <div className="wm-result__row">
              <span className="wm-result__label">Decoded at</span>
              <span className="wm-result__value">{state.decodedAt.toLocaleString()}</span>
            </div>
            <div className="wm-result__actions">
              <Link to={`/users/${state.userId}`} className="wm-result__link">
                View user profile →
              </Link>
              <Button tone="ghost" onClick={reset}>Scan another</Button>
            </div>
          </div>
        )}

        {state.phase === 'empty' && (
          <div className="wm-result wm-result--empty">
            <div className="wm-result__badge wm-result__badge--empty">No watermark found</div>
            <div className="wm-result__filename">{state.filename}</div>
            <p className="wm-result__hint">
              This image does not contain an Ifá watermark. It may have been shared before
              watermarking was enabled, converted to JPEG (which destroys LSB data), or
              screenshotted rather than saved as PNG.
            </p>
            <Button tone="ghost" onClick={reset}>Try another image</Button>
          </div>
        )}
      </Card>
    </div>
  );
}
